<script setup>
import { computed } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, required: true },
  icon: { type: String, required: true },
  iconBgColor: { type: String, required: true },
  iconTextColor: { type: String, required: true },
  selectedSlots: { type: Array, required: true },
  selectedColor: { type: String, default: 'purple' },
  viewOnly: { type: Boolean, default: true },
  isGroupCalendar: { type: Boolean, default: false }, // New prop for group sessions
})

// Predefined color palette (30 light colors) - same as AvailabilityScheduler
const colorPalette = [
  { border: '#93C5FD', bg: '#DBEAFE', text: '#1E40AF' }, // Blue
  { border: '#86EFAC', bg: '#D1FAE5', text: '#065F46' }, // Green
  { border: '#FCA5A5', bg: '#FEE2E2', text: '#991B1B' }, // Red
  { border: '#FCD34D', bg: '#FEF3C7', text: '#92400E' }, // Yellow
  { border: '#C4B5FD', bg: '#EDE9FE', text: '#5B21B6' }, // Purple
  { border: '#F9A8D4', bg: '#FCE7F3', text: '#9F1239' }, // Pink
  { border: '#67E8F9', bg: '#CFFAFE', text: '#155E75' }, // Cyan
  { border: '#FB923C', bg: '#FED7AA', text: '#9A3412' }, // Orange
  { border: '#A78BFA', bg: '#DDD6FE', text: '#6B21A8' }, // Indigo
  { border: '#34D399', bg: '#D1FAE5', text: '#047857' }, // Emerald
  { border: '#F472B6', bg: '#FAE8FF', text: '#A21CAF' }, // Fuchsia
  { border: '#818CF8', bg: '#E0E7FF', text: '#4338CA' }, // Violet
  { border: '#FDBA74', bg: '#FFEDD5', text: '#C2410C' }, // Amber
  { border: '#2DD4BF', bg: '#CCFBF1', text: '#115E59' }, // Teal
  { border: '#FB7185', bg: '#FFE4E6', text: '#BE123C' }, // Rose
  { border: '#BAE6FD', bg: '#E0F2FE', text: '#0C4A6E' }, // Sky
  { border: '#BBF7D0', bg: '#DCFCE7', text: '#14532D' }, // Lime
  { border: '#FED7AA', bg: '#FFEDD5', text: '#7C2D12' }, // Orange Light
  { border: '#E9D5FF', bg: '#F3E8FF', text: '#6B21A8' }, // Purple Light
  { border: '#FBCFE8', bg: '#FCE7F3', text: '#831843' }, // Pink Light
  { border: '#A5F3FC', bg: '#CFFAFE', text: '#164E63' }, // Cyan Light
  { border: '#FDE68A', bg: '#FEF3C7', text: '#78350F' }, // Yellow Light
  { border: '#BFDBFE', bg: '#DBEAFE', text: '#1E3A8A' }, // Blue Light
  { border: '#6EE7B7', bg: '#D1FAE5', text: '#064E3B' }, // Green Light
  { border: '#FCA5A5', bg: '#FEE2E2', text: '#7F1D1D' }, // Red Light
  { border: '#DDD6FE', bg: '#EDE9FE', text: '#4C1D95' }, // Violet Light
  { border: '#FCD6BB', bg: '#FFEDD5', text: '#9A3412' }, // Peach
  { border: '#C7D2FE', bg: '#E0E7FF', text: '#3730A3' }, // Indigo Light
  { border: '#99F6E4', bg: '#CCFBF1', text: '#134E4A' }, // Teal Light
  { border: '#FECACA', bg: '#FEE2E2', text: '#991B1B' }, // Rose Light
]

// Build a map of unique subject-level combinations to color indices from the slots themselves
const subjectLevelColorMap = computed(() => {
  const map = new Map()
  let colorIndex = 0

  // Extract unique subject-level combos from slots
  const seen = new Set()
  props.selectedSlots.forEach(slot => {
    if (slot.subject_id && slot.level_id) {
      const key = `${slot.subject_id}-${slot.level_id}`
      if (!seen.has(key)) {
        seen.add(key)
        map.set(key, colorIndex % colorPalette.length)
        colorIndex++
      }
    }
  })

  return map
})

// Get color for a subject-level combination
const getSubjectLevelColor = (subjectId, levelId) => {
  const key = `${subjectId}-${levelId}`
  const colorIndex = subjectLevelColorMap.value.get(key) ?? 0
  return colorPalette[colorIndex]
}

// Get dynamic button styles based on subject-level color
const getButtonStyles = (day, time) => {
  if (!isSlotSelected(day, time)) return {}

  if (props.isGroupCalendar) {
    const slotData = getSlotData(day, time)
    if (slotData && slotData.subject_id && slotData.level_id) {
      const colors = getSubjectLevelColor(slotData.subject_id, slotData.level_id)
      return {
        borderColor: colors.border,
        backgroundColor: colors.bg,
        color: colors.text
      }
    }
  }

  return {}
}

// Days and time slots configuration
const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const dayLabels = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday'
}

// Extended hours from 8am to 9pm
const timeSlots = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00']
const timeLabels = {
  '08:00': '8 AM',
  '09:00': '9 AM',
  '10:00': '10 AM',
  '11:00': '11 AM',
  '12:00': '12 PM',
  '13:00': '1 PM',
  '14:00': '2 PM',
  '15:00': '3 PM',
  '16:00': '4 PM',
  '17:00': '5 PM',
  '18:00': '6 PM',
  '19:00': '7 PM',
  '20:00': '8 PM',
  '21:00': '9 PM',
}

const buttonColors = computed(() => {
  const colors = {
    purple: {
      selected: 'border-purple-600 bg-purple-600 text-white font-semibold',
      unselected: 'border-gray-200 bg-white text-gray-400'
    },
    yellow: {
      selected: 'border-yellow-600 bg-yellow-600 text-white font-semibold',
      unselected: 'border-gray-200 bg-white text-gray-400'
    }
  }
  return colors[props.selectedColor] || colors.purple
})

const isSlotSelected = (day, time) => {
  if (props.isGroupCalendar) {
    // For group calendar, check if any slot matches day and time
    return props.selectedSlots.some(slot =>
      slot.day?.toLowerCase() === day && slot.time === time
    )
  }
  // For simple string arrays (1:1 availability)
  return props.selectedSlots.includes(`${day}-${time}`)
}

const getSlotData = (day, time) => {
  if (!props.isGroupCalendar) return null
  return props.selectedSlots.find(slot =>
    slot.day?.toLowerCase() === day && slot.time === time
  )
}
</script>

<template>
  <div class="select-none">
    <div class="flex items-center gap-3 mb-4">
      <div :class="['w-10 h-10 rounded-lg flex items-center justify-center', iconBgColor]">
        <span :class="['material-symbols-outlined', iconTextColor]">{{ icon }}</span>
      </div>
      <div>
        <h4 class="font-semibold text-gray-900">{{ title }}</h4>
        <p class="text-xs text-gray-500">{{ description }}</p>
      </div>
    </div>
    <div class="grid grid-cols-7 gap-3">
      <div v-for="day in daysOfWeek" :key="day" class="text-center">
        <p class="text-xs font-semibold text-gray-600 mb-3">{{ dayLabels[day] }}</p>
        <div class="space-y-2">
          <div
            v-for="time in timeSlots"
            :key="time"
            :class="[
              'w-full h-10 px-2 text-xs border-2 rounded-lg transition-colors cursor-default flex flex-col items-center justify-center',
              isSlotSelected(day, time) && isGroupCalendar
                ? 'font-semibold'
                : isSlotSelected(day, time)
                ? buttonColors.selected
                : buttonColors.unselected
            ]"
            :style="isSlotSelected(day, time) && isGroupCalendar ? getButtonStyles(day, time) : {}"
          >
            <template v-if="isGroupCalendar && isSlotSelected(day, time)">
              <div class="truncate w-full text-center uppercase font-semibold text-[10px] leading-tight" :title="getSlotData(day, time)?.subject_name">
                {{ getSlotData(day, time)?.subject_name }}
              </div>
              <div class="truncate w-full text-center opacity-80 text-[9px] leading-tight" :title="getSlotData(day, time)?.level_name">
                {{ getSlotData(day, time)?.level_name }}
              </div>
            </template>
            <template v-else>
              {{ timeLabels[time] }}
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
